import { headPreparation, headPreparationKeys, type HeadPreparation } from './head-preparation.js';
import { dropHeadSurfaces, hasHeadCache, stockHeadSurfaces, type HeadCacheEntries, type HeadDetail } from './human-head.js';
import { headedDefinition } from './create-unit-animator.js';
import type { UnitDefinition } from '../../../shared/game/world-types.js';
import { dropFigureTape, installFigureTape } from './figure-tape.js';
import type { FigureBake } from './figure-bakes.js';

/** One figure's missing head surfaces, meshed by one worker; `owners` counts the stocks that still need them. */
interface Job { request: HeadPreparation; missing: string[]; owners: number; started: boolean; meshed: string[]; done: Promise<void>; finish(): void }
const queue: Job[] = [];
/** The job that is meshing, or meshed, each surface until no stock needs it, so stocks asking for the same surfaces share it. */
const jobsByKey = new Map<string, Job>();
let workers = 0, broken = false;
/** Workers mesh beside the page, which keeps a core to build figures and the map meanwhile. */
const workerLimit = () => Math.max(1, Math.min(8, (navigator.hardwareConcurrency || 4) - 1));

function forget(job: Job) {
  for (const key of [...job.missing, ...job.meshed]) if (jobsByKey.get(key) === job) jobsByKey.delete(key);
  dropHeadSurfaces(job.meshed);
}
function work(worker: Worker) {
  const job = queue.shift();
  if (!job) { worker.terminate(); workers--; return; }
  job.started = true;
  worker.onmessage = (event: MessageEvent<{ entries?: HeadCacheEntries }>) => {
    const entries = event.data.entries ?? [];
    if (job.owners > 0) {
      stockHeadSurfaces(entries); job.meshed = entries.map(([key]) => key);
      for (const key of job.meshed) if (!jobsByKey.has(key)) jobsByKey.set(key, job);
    }
    job.finish(); work(worker);
  };
  worker.postMessage({ request: job.request, missing: job.missing, known: [] });
}
function pump() {
  while (queue.length && workers < workerLimit() && !broken) {
    let worker: Worker;
    try { worker = new Worker(new URL('./head-mesh.worker.ts', import.meta.url), { type: 'module' }); }
    catch { broken = true; break; }
    workers++;
    // A worker that cannot run leaves every figure to mesh its own head, as it would without the pool.
    worker.onerror = () => { worker.terminate(); workers--; broken = true; for (const job of [...queue.splice(0), ...jobsByKey.values()]) job.finish(); };
    work(worker);
  }
  if (broken) for (const job of queue.splice(0)) job.finish();
}

export interface HeadStock {
  /** Settles once every surface asked for is stocked, or has failed and is left to the figure to mesh. */
  ready: Promise<void>;
  /** The same for one figure, by its definition's id. */
  of(id: string): Promise<void>;
  /** Called once the figures are built: forgets the surfaces no other stock still needs, and drops jobs not yet started. */
  release(): void;
}

/** Where a figure's bake comes from (`figure-bakes.ts`): the browser's list of bakes, kept apart so tests and tools need none. */
export interface FigureBakeSource {
  key(definition: UnitDefinition, detail: HeadDetail): string | null;
  load(definition: UnitDefinition, detail: HeadDetail): Promise<FigureBake | null>;
}

/** Worker jobs meshing whatever of `request` no job or cache has yet, sharing the jobs other stocks already started. */
function meshJobs(request: HeadPreparation) {
  const missing: string[] = [], needs = new Set<Job>();
  for (const key of headPreparationKeys(request)) {
    const shared = jobsByKey.get(key);
    if (shared) needs.add(shared); else if (!hasHeadCache(key)) missing.push(key);
  }
  if (missing.length) {
    let finish = () => {};
    const job: Job = { request, missing, owners: 0, started: false, meshed: [], done: new Promise<void>(resolve => { finish = resolve; }), finish: () => finish() };
    for (const key of missing) jobsByKey.set(key, job);
    queue.push(job); needs.add(job);
  }
  return needs;
}

/**
 * Meshes the heads, hair and beards of figures about to be built on parallel workers, and stocks them (`stockHeadSurfaces`) so building
 * those figures skips their slowest part: about two thirds of a figure, a few hundred milliseconds each, which a map of residents
 * otherwise spends one after another on the page. Surfaces are identical to those the figure would mesh itself. A figure baked at build
 * time (`baked`) is fetched instead, heads and skin, and only meshed if its bake cannot be read.
 */
export function stockFigureHeads(definitions: readonly UnitDefinition[], detail: HeadDetail, baked?: FigureBakeSource): HeadStock {
  const jobs = new Set<Job>(), figures = new Map<string, Promise<void>>(), loads: Promise<void>[] = [];
  if (typeof Worker === 'undefined') return { ready: Promise.resolve(), of: () => Promise.resolve(), release() {} };
  let released = false;
  const own = (needs: Set<Job>) => { for (const job of needs) if (!jobs.has(job)) { jobs.add(job); job.owners++; } pump(); return Promise.all([...needs].map(job => job.done)).then(() => {}); };
  // One fetch for every figure of a bake, and its tape replayed once for each of them.
  const bakes = new Map<string, { definition: UnitDefinition; request: HeadPreparation; uses: number; loaded?: Promise<void> }>(), stocked: string[] = [], tapes = new Set<string>();
  for (const definition of definitions) {
    const headed = headedDefinition(definition); if (!headed) continue;
    let request: HeadPreparation;
    try { request = headPreparation(headed, detail); } catch { continue; }
    const key = baked?.key(headed, detail);
    if (key) {
      const bake = bakes.get(key) ?? { definition: headed, request, uses: 0 };
      bake.uses++; bakes.set(key, bake);
      bake.loaded ??= baked!.load(headed, detail).then(async figure => {
        if (released) return;
        if (figure) {
          stockHeadSurfaces(figure.heads); stocked.push(...figure.heads.map(([surface]) => surface));
          installFigureTape(figure.key, figure.tape, bake.uses); tapes.add(figure.key);
        }
        // Whatever the bake did not bring is meshed as an unbaked figure's would be.
        const needs = meshJobs(bake.request); if (needs.size) await own(needs);
      });
      loads.push(bake.loaded); figures.set(definition.id, bake.loaded);
      continue;
    }
    const needs = meshJobs(request); if (!needs.size) continue;
    figures.set(definition.id, own(needs));
  }
  return {
    ready: Promise.all([...loads, ...[...jobs].map(job => job.done)]).then(() => {}),
    of: id => figures.get(id) ?? Promise.resolve(),
    release() {
      if (released) return; released = true;
      for (const job of jobs) {
        if (--job.owners > 0) continue;
        if (!job.started) { const at = queue.indexOf(job); if (at >= 0) queue.splice(at, 1); job.finish(); }
        forget(job);
      }
      dropHeadSurfaces(stocked); for (const key of tapes) dropFigureTape(key);
    },
  };
}
